import { useState } from "react";
import {
  CalendarDays, CheckCircle2, XCircle, Clock, FileText, ChevronDown, ChevronRight, Ban, UserPlus,
} from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { formatDate } from "@/utils/formatDate";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Modal } from "@/components/ui/Modal";
import { PageLoader } from "@/components/ui/Spinner";
import { useLeaveRequests, useApproveLeave, useRejectLeave, useApproveCancellationLeave, useLeaveProxyTargets } from "../hooks/useLeaves";
import { ProxyLeaveSubmitModal } from "../components/ProxyLeaveSubmitModal";
import { EmployeePickerContent } from "../components/EmployeePickerContent";
import { LEAVE_TYPE_LABELS, LEAVE_STATUS_COLORS, fmtHourlyDuration, fmtPartTimeDuration, type LeaveRequest } from "../types";

type StatusFilter = "all" | "pending" | "cancellation_pending" | "approved" | "rejected" | "cancelled";

function fmtDays(req: LeaveRequest): string {
  if (req.is_hourly) return fmtHourlyDuration(req.hours_count, req.minutes_count);
  const days = req.days_count;
  const base = `${days} ${days === 1 ? "dzień roboczy" : "dni robocze"}`;
  const pt = fmtPartTimeDuration(days, req.employee_fraction_numerator, req.employee_fraction_denominator);
  return pt ? `${base}, ${pt}` : base;
}

export function LeaveReviewPage() {
  const user = useAuthStore((s) => s.user);
  // Reviewers may approve/reject. Coordinators may only file on behalf of others
  // and view what they submitted — never approve or reject.
  const canReview = !!user?.is_ceo || user?.role === "director" || user?.role === "hr" || user?.role === "accountant";
  const canProxy = canReview || user?.role === "coordinator";
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const params = statusFilter !== "all" ? { status: statusFilter } : undefined;
  const { data: requests = [], isLoading } = useLeaveRequests(params);
  const approveLeave = useApproveLeave();
  const rejectLeave = useRejectLeave();
  const approveCancellation = useApproveCancellationLeave();

  const { data: proxyTargets = [] } = useLeaveProxyTargets(canProxy);
  const [proxyPickerOpen, setProxyPickerOpen] = useState(false);
  const [proxySearch, setProxySearch] = useState("");
  const [proxyEmployee, setProxyEmployee] = useState<{ id: string; name: string; contractType: string | null } | null>(null);

  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [approveModal, setApproveModal] = useState<LeaveRequest | null>(null);
  const [approveComment, setApproveComment] = useState("");
  const [approveError, setApproveError] = useState("");

  const [rejectModal, setRejectModal] = useState<LeaveRequest | null>(null);
  const [rejectComment, setRejectComment] = useState("");
  const [rejectError, setRejectError] = useState("");

  const [cancellationModal, setCancellationModal] = useState<LeaveRequest | null>(null);
  const [cancellationError, setCancellationError] = useState("");

  const toggle = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const submitApprove = () => {
    if (!approveModal) return;
    setApproveError("");
    approveLeave.mutate(
      { id: approveModal.id, comment: approveComment },
      {
        onSuccess: () => { setApproveModal(null); setApproveComment(""); },
        onError: (err: unknown) => {
          const detail = (err as { response?: { data?: { errors?: { detail?: string } } } })
            ?.response?.data?.errors?.detail;
          setApproveError(detail ?? "Błąd podczas zatwierdzania. Spróbuj ponownie.");
        },
      }
    );
  };

  const submitReject = () => {
    if (!rejectModal) return;
    if (!rejectComment.trim()) { setRejectError("Komentarz jest wymagany przy odrzuceniu."); return; }
    setRejectError("");
    rejectLeave.mutate(
      { id: rejectModal.id, comment: rejectComment },
      {
        onSuccess: () => { setRejectModal(null); setRejectComment(""); },
        onError: () => setRejectError("Błąd podczas odrzucania. Spróbuj ponownie."),
      }
    );
  };

  const submitApproveCancellation = () => {
    if (!cancellationModal) return;
    setCancellationError("");
    approveCancellation.mutate(cancellationModal.id, {
      onSuccess: () => { setCancellationModal(null); },
      onError: () => setCancellationError("Błąd podczas anulowania urlopu. Spróbuj ponownie."),
    });
  };

  const pendingCount = requests.filter((r) => r.status === "pending" || r.status === "cancellation_pending").length;

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Wnioski urlopowe</h1>
            <div className="w-8 h-0.5 bg-[var(--color-primary)] mt-1 mb-6" />
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {canReview
              ? "Zatwierdzanie i odrzucanie wniosków urlopowych pracowników."
              : "Składanie wniosków urlopowych za pracowników i podgląd złożonych przez Ciebie."}
          </p>
          {canReview && (
            <p className="text-xs text-[var(--color-primary)] font-medium mt-0.5">
              {user?.is_ceo || user?.role === "hr" || user?.role === "accountant"
                ? "Wszystkie oddziały"
                : user?.branch?.name
                ? `Oddział: ${user.branch.name}`
                : null}
            </p>
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {pendingCount > 0 && (statusFilter === "pending" || statusFilter === "cancellation_pending") && (
            <span className="text-sm bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 px-3 py-1 rounded-full font-medium">
              {pendingCount} {pendingCount === 1 ? "oczekuje" : "oczekują"}
            </span>
          )}
          {canProxy && (
            <Button variant="secondary" onClick={() => { setProxySearch(""); setProxyPickerOpen(true); }}>
              <UserPlus size={14} className="mr-1.5" /> Złóż wniosek pracownika
            </Button>
          )}
        </div>
      </div>

      {/* Filter tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 dark:bg-gray-800 rounded-lg p-1 w-fit flex-wrap">
        {(["pending", "cancellation_pending", "approved", "rejected", "cancelled", "all"] as StatusFilter[]).map((s) => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
              statusFilter === s
                ? "bg-white dark:bg-gray-700 text-[var(--color-primary)] shadow-sm"
                : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            }`}
          >
            {s === "pending" ? "Oczekujące" :
             s === "cancellation_pending" ? "Prośby o anulowanie" :
             s === "approved" ? "Zatwierdzone" :
             s === "rejected" ? "Odrzucone" :
             s === "cancelled" ? "Anulowane" : "Wszystkie"}
          </button>
        ))}
      </div>

      {isLoading ? (
        <PageLoader />
      ) : requests.length === 0 ? (
        <div className="bg-white dark:bg-gray-800 p-8 text-center">
          <CalendarDays size={32} className="text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-gray-400 dark:text-gray-500 text-sm">Brak wniosków w tej kategorii.</p>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          {requests.map((req) => {
            const isOpen = expanded.has(req.id);
            return (
              <div
                key={req.id}
                className="bg-white dark:bg-gray-800 overflow-hidden"
              >
                {/* Header */}
                <div className="flex items-center gap-3 px-4 py-3">
                  <button
                    onClick={() => toggle(req.id)}
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                  >
                    {isOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                  </button>

                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-sm text-gray-900 dark:text-gray-100">
                        {req.employee_name}
                      </span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {LEAVE_TYPE_LABELS[req.leave_type]}
                      </span>
                      <Badge color={LEAVE_STATUS_COLORS[req.status]}>
                        {req.status === "approved" && <CheckCircle2 size={11} className="mr-1" />}
                        {req.status === "rejected" && <XCircle size={11} className="mr-1" />}
                        {req.status === "pending" && <Clock size={11} className="mr-1" />}
                        {req.status === "cancellation_pending" && <Ban size={11} className="mr-1" />}
                        {req.status === "cancelled" && <XCircle size={11} className="mr-1" />}
                        {req.status_display}
                      </Badge>
                      {req.submitted_by_name && (
                        <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                          złożone przez {req.submitted_by_name}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {req.is_hourly ? formatDate(req.start_date) : `${formatDate(req.start_date)} – ${formatDate(req.end_date)}`}
                      <span className="ml-2 text-gray-400">({fmtDays(req)})</span>
                      {req.available_annual_days != null && !req.is_hourly && (
                        <span className={`ml-2 font-medium ${req.available_annual_days < req.days_count ? "text-red-500 dark:text-red-400" : "text-emerald-600 dark:text-emerald-400"}`}>
                          · dostępne: {req.available_annual_days}d
                          {(req.available_annual_hours ?? 0) > 0 && ` ${req.available_annual_hours}h`}
                          {(req.available_annual_minutes ?? 0) > 0 && ` ${req.available_annual_minutes}min`}
                        </span>
                      )}
                      {req.is_hourly && req.available_annual_days != null && (
                        <span className="ml-2 font-medium text-emerald-600 dark:text-emerald-400">
                          · dostępne: {req.available_annual_days}d
                          {(req.available_annual_hours ?? 0) > 0 && ` ${req.available_annual_hours}h`}
                          {(req.available_annual_minutes ?? 0) > 0 && ` ${req.available_annual_minutes}min`}
                        </span>
                      )}
                      <span className="ml-2 text-gray-400">
                        · złożono {new Date(req.created_at).toLocaleDateString("pl-PL")}
                      </span>
                    </p>
                  </div>

                  {/* Quick actions — reviewers only */}
                  {canReview && req.status === "pending" && (
                    <div className="flex gap-2 flex-wrap flex-shrink-0">
                      <button
                        onClick={() => { setApproveComment(""); setApproveError(""); setApproveModal(req); }}
                        className="flex items-center gap-1 text-xs bg-green-600 text-white px-2.5 py-1.5 rounded-md hover:bg-green-700 font-medium"
                      >
                        <CheckCircle2 size={12} /> Zatwierdź
                      </button>
                      <button
                        onClick={() => { setRejectComment(""); setRejectError(""); setRejectModal(req); }}
                        className="flex items-center gap-1 text-xs bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 px-2.5 py-1.5 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 font-medium"
                      >
                        <XCircle size={12} /> Odrzuć
                      </button>
                    </div>
                  )}
                  {canReview && req.status === "cancellation_pending" && (
                    <button
                      onClick={() => { setCancellationError(""); setCancellationModal(req); }}
                      className="flex items-center gap-1 text-xs bg-red-600 text-white px-2.5 py-1.5 rounded-md hover:bg-red-700 font-medium flex-shrink-0"
                    >
                      <Ban size={12} /> Anuluj urlop
                    </button>
                  )}
                </div>

                {/* Expanded details */}
                {isOpen && (
                  <div className="border-t border-gray-100 dark:border-gray-700 px-4 py-4 bg-gray-50 dark:bg-gray-800/50 text-sm flex flex-col gap-2">
                    {req.status === "cancellation_pending" && req.cancellation_comment && (
                      <p className="text-yellow-700 dark:text-yellow-400">
                        <span className="font-medium">Powód anulowania: </span>
                        {req.cancellation_comment}
                      </p>
                    )}
                    {req.note && (
                      <p className="text-gray-600 dark:text-gray-400">
                        <span className="font-medium text-gray-700 dark:text-gray-300">Uwagi: </span>
                        {req.note}
                      </p>
                    )}
                    {req.review_comment && (
                      <p className={req.status === "rejected" ? "text-red-600" : "text-green-700"}>
                        <span className="font-medium">Komentarz: </span>
                        {req.review_comment}
                      </p>
                    )}
                    {req.reviewed_by_name && (
                      <p className="text-gray-500 dark:text-gray-400 text-xs">
                        Decyzja: {req.reviewed_by_name}
                        {req.reviewed_at && ` · ${new Date(req.reviewed_at).toLocaleString("pl-PL")}`}
                      </p>
                    )}
                    {req.attachment_managed_file_detail?.url && (
                      <>
                        <a
                          href={req.attachment_managed_file_detail.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1.5 text-[var(--color-primary)] hover:underline w-fit"
                        >
                          <FileText size={14} /> Pobierz skan wniosku
                        </a>
                        {req.attachment_managed_file_detail.url.toLowerCase().split("?")[0].endsWith(".pdf") ? (
                          <iframe
                            src={req.attachment_managed_file_detail.inline_url ?? req.attachment_managed_file_detail.url}
                            title="Skan wniosku"
                            className="w-full rounded mt-2"
                            style={{ height: "600px" }}
                          />
                        ) : (
                          <img
                            src={req.attachment_managed_file_detail.inline_url ?? req.attachment_managed_file_detail.url}
                            alt="Skan wniosku"
                            className="max-w-full rounded mt-2"
                          />
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Approve modal */}
      <Modal open={!!approveModal} onClose={() => setApproveModal(null)} title="Zatwierdź wniosek urlopowy">
        <div className="flex flex-col gap-4">
          {approveModal && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-green-800 dark:text-green-300">{approveModal.employee_name}</p>
              <p className="text-green-700 dark:text-green-400 text-xs mt-0.5">
                {LEAVE_TYPE_LABELS[approveModal.leave_type]} · {approveModal.is_hourly ? formatDate(approveModal.start_date) : `${formatDate(approveModal.start_date)} – ${formatDate(approveModal.end_date)}`}
                <span className="ml-1">
                  ({fmtDays(approveModal)})
                </span>
              </p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Komentarz <span className="text-gray-400 text-xs">(opcjonalny)</span>
            </label>
            <textarea
              rows={2}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={approveComment}
              onChange={(e) => setApproveComment(e.target.value)}
              placeholder="Dodatkowe uwagi..."
            />
          </div>
          {approveError && <p className="text-sm text-red-600">{approveError}</p>}
          <div className="flex gap-3">
            <Button loading={approveLeave.isPending} onClick={submitApprove}>
              <CheckCircle2 size={14} className="mr-1.5" /> Zatwierdź
            </Button>
            <Button variant="secondary" onClick={() => setApproveModal(null)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      {/* Reject modal */}
      <Modal open={!!rejectModal} onClose={() => setRejectModal(null)} title="Odrzuć wniosek urlopowy">
        <div className="flex flex-col gap-4">
          {rejectModal && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-red-800 dark:text-red-300">{rejectModal.employee_name}</p>
              <p className="text-red-700 dark:text-red-400 text-xs mt-0.5">
                {LEAVE_TYPE_LABELS[rejectModal.leave_type]} · {formatDate(rejectModal.start_date)} – {formatDate(rejectModal.end_date)}
              </p>
            </div>
          )}
          <div>
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300 block mb-1">
              Powód odrzucenia <span className="text-red-500">*</span>
            </label>
            <textarea
              rows={3}
              className="block w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm"
              value={rejectComment}
              onChange={(e) => setRejectComment(e.target.value)}
              placeholder="Uzasadnienie odrzucenia wniosku..."
            />
            {rejectError && <p className="text-xs text-red-500 mt-1">{rejectError}</p>}
          </div>
          <div className="flex gap-3">
            <Button loading={rejectLeave.isPending} onClick={submitReject} variant="secondary">
              <XCircle size={14} className="mr-1.5" /> Odrzuć wniosek
            </Button>
            <Button variant="secondary" onClick={() => setRejectModal(null)}>Anuluj</Button>
          </div>
        </div>
      </Modal>

      {/* Approve cancellation modal */}
      <Modal open={!!cancellationModal} onClose={() => setCancellationModal(null)} title="Zatwierdź anulowanie urlopu">
        <div className="flex flex-col gap-4">
          {cancellationModal && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm">
              <p className="font-medium text-red-800 dark:text-red-300">{cancellationModal.employee_name}</p>
              <p className="text-red-700 dark:text-red-400 text-xs mt-0.5">
                {LEAVE_TYPE_LABELS[cancellationModal.leave_type]} · {cancellationModal.is_hourly ? formatDate(cancellationModal.start_date) : `${formatDate(cancellationModal.start_date)} – ${formatDate(cancellationModal.end_date)}`}
                <span className="ml-1">
                  ({fmtDays(cancellationModal)})
                </span>
              </p>
              {cancellationModal.cancellation_comment && (
                <p className="text-yellow-700 dark:text-yellow-400 text-xs mt-1 italic">
                  Powód: {cancellationModal.cancellation_comment}
                </p>
              )}
            </div>
          )}
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Po zatwierdzeniu urlop zostanie anulowany, a dni urlopowe wrócą do puli pracownika.
          </p>
          {cancellationError && <p className="text-sm text-red-600">{cancellationError}</p>}
          <div className="flex gap-3">
            <Button loading={approveCancellation.isPending} onClick={submitApproveCancellation} variant="secondary">
              <Ban size={14} className="mr-1.5" /> Zatwierdź anulowanie
            </Button>
            <Button variant="secondary" onClick={() => setCancellationModal(null)}>Zamknij</Button>
          </div>
        </div>
      </Modal>

      {/* Proxy submit — employee picker */}
      <Modal
        open={proxyPickerOpen}
        onClose={() => { setProxyPickerOpen(false); setProxySearch(""); }}
        title="Złóż wniosek urlopowy — wybierz pracownika"
        size="lg"
      >
        <EmployeePickerContent
          targets={proxyTargets}
          search={proxySearch}
          onSearchChange={setProxySearch}
          onSelect={(t) => {
            setProxyEmployee({ id: t.id, name: t.full_name, contractType: t.contract_type });
            setProxyPickerOpen(false);
            setProxySearch("");
          }}
          onCancel={() => { setProxyPickerOpen(false); setProxySearch(""); }}
        />
      </Modal>

      {/* Proxy submit — leave request form on behalf of the chosen employee */}
      {proxyEmployee && (
        <ProxyLeaveSubmitModal
          open={!!proxyEmployee}
          onClose={() => setProxyEmployee(null)}
          employee={proxyEmployee}
        />
      )}
    </div>
  );
}
